import React, { useState, useEffect, useMemo } from 'react';
import { 
  Form, Input, Select, message, Card, Row, Col, Divider, 
  InputNumber, Button, Descriptions, Modal, Spin, Tag, AutoComplete
} from 'antd';
import { PlusOutlined, ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { orderAPI, dropshipperAPI, exchangeRateAPI, userAPI, addressAPI, productAPI } from '../api';
import { normalizeMoney } from '../utils/money';
import countryList from 'react-select-country-list';
import { useNavigate, useParams } from 'react-router-dom';
import ProductSearchSelector from '../utils/ProductSearchSelector';

const { Option } = Select;
const { TextArea } = Input;
const { Search } = Input;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const AdminEditOrder = () => {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const isEditMode = Boolean(orderNumber);
  const [form] = Form.useForm();
  const [addressForm] = Form.useForm();
  
  // States
  const [loading, setLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(isEditMode);
  const [orderData, setOrderData] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionMap, setUserOptionMap] = useState({});
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [myProducts, setMyProducts] = useState([]);
  const [freightChannels, setFreightChannels] = useState([]);
  const [myCustomizations, setMyCustomizations] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressFormModalVisible, setAddressFormModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [productSelectorVisible, setProductSelectorVisible] = useState(false);
  const [productSelectorIndex, setProductSelectorIndex] = useState(null);
  
  // Exchange rates for freight calculation
  const [exchangeRates, setExchangeRates] = useState({});
  
  // 获取国家列表数据
  const countries = countryList();
  const countryOptions = countries.getData();

  const getUserRoleTag = (role) => {
    const roleConfig = {
      admin: '管理员',
      dropshipper: 'Dropshipper',
    };
    return `[${roleConfig[role] || role}]`;
  };

  const renderUserOptionLabel = (user) =>
    `${getUserRoleTag(user.role)} ${user.username}${user.email ? ` (${user.email})` : ''}`;

  const userSelectOptions = useMemo(() => {
    const base = userOptions || [];
    const extended = selectedUserId && !base.some(user => user?.id === selectedUserId)
      ? [...base, userOptionMap[selectedUserId]].filter(Boolean)
      : base;
    return extended.map(user => ({
      value: String(user.id),
      label: renderUserOptionLabel(user),
    }));
  }, [userOptions, userOptionMap, selectedUserId]);

  useEffect(() => {
    fetchExchangeRates();

    if (isEditMode) {
      fetchOrderData();
    } else {
      setOrderLoading(false);
      setSelectedUserId(null);
      setUserSearchInput('');
      setUserOptions([]);
      setUserOptionMap({});
      setSavedAddresses([]);
      setSelectedAddressId(null);
      setEditingAddress(null);
      form.setFieldsValue({
        order_items: [{
          product_id: undefined,
          variant_path: undefined,
          selected_variant_key: undefined,
          quantity: 1,
          freight_id: undefined,
          customization_id: undefined
        }]
      });
    }
  }, [orderNumber, isEditMode]);

  // Effect to handle user selection and fetch their data
  useEffect(() => {
    if (selectedUserId) {
      setMyProducts([]);
      fetchUserCustomizations();
      fetchFreightChannels();

      if (!isEditMode) {
        fetchSavedAddresses();
      } else {
        setSavedAddresses([]);
        setSelectedAddressId(null);
      }
    } else {
      // Clear data when no user is selected
      setMyProducts([]);
      setMyCustomizations([]);
      setFreightChannels([]);

      if (!isEditMode) {
        setSavedAddresses([]);
        setSelectedAddressId(null);
        form.setFieldsValue({
          customer_name: undefined,
          customer_email: undefined,
          customer_phone: undefined,
          dest_country: undefined,
          shipping_address: undefined,
        });
      }
    }
  }, [selectedUserId, isEditMode]);

  // 当订单数据加载完成后，获取订单中的商品详情
  useEffect(() => {
    if (orderData && isEditMode) {
      loadOrderProducts();
    }
  }, [orderData, isEditMode]);

  const fetchOrderData = async () => {
    if (!orderNumber) return;

    try {
      setOrderLoading(true);
      const response = await orderAPI.getByNumber(orderNumber);
      const order = response.data;
      setOrderData(order);
      
      // 设置选中的用户
      if (order.dropshipper_id) {
        setSelectedUserId(order.dropshipper_id);
        if (order.dropshipper) {
          setUserOptionMap(prev => ({
            ...prev,
            [order.dropshipper.id]: order.dropshipper,
          }));
          setUserOptions(prev => {
            if (prev.some(user => user.id === order.dropshipper.id)) {
              return prev;
            }
            return [...prev, order.dropshipper];
          });
          setUserSearchInput(renderUserOptionLabel(order.dropshipper));
        }
      }
      
    } catch (error) {
      
      message.error('获取订单数据失败');
      navigate('/admin/orders');
    } finally {
      setOrderLoading(false);
    }
  };

  const loadOrderProducts = async () => {
    if (!orderData || !orderData.items_detail || orderData.items_detail.length === 0) {
      return;
    }

    try {
      // 获取订单中所有商品的详情
      const productIds = [...new Set(orderData.items_detail.map(item => item.product_id))];
      const productPromises = productIds.map(id => productAPI.getByIdAuth(id));
      const productResponses = await Promise.all(productPromises);
      
      // 构建商品映射
      const productsMap = {};
      productResponses.forEach(response => {
        const product = response.data;
        productsMap[product.id] = {
          id: product.id,
          product_id: product.id,
          product: product,
          is_active: product.is_active,
          custom_price: null,
          freight_id: null,
          customization_id: null,
        };
      });

      // 更新myProducts，将新加载的商品添加到列表中
      setMyProducts(prevProducts => {
        const existingIds = new Set(prevProducts.map(p => p.product_id));
        const newProducts = Object.values(productsMap).filter(p => !existingIds.has(p.product_id));
        return [...prevProducts, ...newProducts];
      });

      // 初始化表单数据
      form.setFieldsValue({
        customer_name: orderData.customer_name,
        customer_email: orderData.customer_email,
        customer_phone: orderData.customer_phone,
        dest_country: orderData.dest_country,
        shipping_address: orderData.shipping_address,
        message: orderData.message,
        order_items: orderData.items_detail?.map(item => {
          // 需要根据variant_path找到对应的selected_variant_key
          const product = productsMap[item.product_id];
          let selectedVariantKey = null;
          
          if (product?.product?.variants && Array.isArray(product.product.variants)) {
            const variant = product.product.variants.find(v => 
              JSON.stringify(v.variant_path || []) === JSON.stringify(item.variant_path || [])
            );
            if (variant) {
              selectedVariantKey = variant.id ?? variant.SKU ?? 0;
            }
          }
          
          return {
            product_id: item.product_id,
            variant_path: item.variant_path || [],
            selected_variant_key: selectedVariantKey,
            quantity: item.quantity,
            freight_id: item.freight_id,
            customization_id: item.customization_id || null
          };
        }) || []
      });
      
      // 触发价格预览更新
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
      }, 100);
    } catch (error) {
      
      message.error('加载订单商品信息失败');
    }
  };

  const handleUserSearch = async (value) => {
    const query = (value || '').trim();
    setUserSearchInput(value);
    if (!query) {
      setUserOptions([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const response = await userAPI.getAll({
        page: 1,
        page_size: 20,
        search: query,
        is_active: true,
      });
      const userList = (response.data?.data || []).filter(user => user.is_active);
      setUserOptions(userList);
      setUserOptionMap(prev => {
        const next = { ...prev };
        userList.forEach(user => {
          next[user.id] = user;
        });
        return next;
      });
    } catch (error) {
      
      message.error('搜索用户失败');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleUserSelect = (value, option) => {
    const userId = Number(value);
    setSelectedUserId(Number.isNaN(userId) ? null : userId);
    const matchedUser =
      userOptions.find(user => user.id === userId) ||
      userOptionMap[userId];
    if (matchedUser) {
      setUserOptionMap(prev => ({
        ...prev,
        [userId]: matchedUser,
      }));
      setUserSearchInput(option?.label ?? renderUserOptionLabel(matchedUser));
    } else {
      setUserSearchInput(option?.label ?? '');
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await exchangeRateAPI.getAll();
      const rates = {};
      response.data.forEach(rate => {
        rates[rate.currency_code] = rate.rate_to_usd;
      });
      setExchangeRates(rates);
    } catch (error) {
      
    }
  };

  const fetchFreightChannels = async () => {
    try {
      const response = await dropshipperAPI.getFreightChannels();
      setFreightChannels(response.data);
    } catch (error) {
      
    }
  };

  const fetchUserCustomizations = async () => {
    if (!selectedUserId) return;
    
    try {
      // Get customizations for the selected user
      const response = await dropshipperAPI.getMyCustomizations(selectedUserId);
      setMyCustomizations(response.data);
    } catch (error) {
      
      // If no customizations found for user, clear the list
      setMyCustomizations([]);
    }
  };

  const fetchSavedAddresses = async () => {
    if (!selectedUserId || isEditMode) return;

    try {
      const response = await addressAPI.getUserAddresses(selectedUserId);
      setSavedAddresses(response.data);

      const defaultAddress = response.data.find(addr => addr.is_default);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
        form.setFieldsValue({
          customer_name: defaultAddress.customer_name,
          customer_email: defaultAddress.customer_email,
          customer_phone: defaultAddress.customer_phone,
          dest_country: defaultAddress.country,
          shipping_address: defaultAddress.address,
        });

        setRefreshKey(prev => prev + 1);
        setTimeout(() => {
          onCountryChange(defaultAddress.country);
        }, 100);
      } else {
        setSelectedAddressId(null);
      }
    } catch (error) {
      
      setSavedAddresses([]);
      setSelectedAddressId(null);
    }
  };

  const handleAddressSelect = (addressId) => {
    if (isEditMode) return;

    setSelectedAddressId(addressId);
    const selectedAddress = savedAddresses.find(addr => addr.id === addressId);
    if (selectedAddress) {
      form.setFieldsValue({
        customer_name: selectedAddress.customer_name,
        customer_email: selectedAddress.customer_email,
        customer_phone: selectedAddress.customer_phone,
        dest_country: selectedAddress.country,
        shipping_address: selectedAddress.address,
      });

      setRefreshKey(prev => prev + 1);
      setTimeout(() => {
        onCountryChange(selectedAddress.country);
      }, 100);
    }
  };

  const handleNewAddress = () => {
    if (isEditMode) return;
    setEditingAddress(null);
    addressForm.resetFields();
    setAddressFormModalVisible(true);
  };

  const handleEditAddress = () => {
    if (isEditMode) return;
    if (selectedAddressId) {
      const address = savedAddresses.find(addr => addr.id === selectedAddressId);
      if (address) {
        setEditingAddress(address);
        addressForm.setFieldsValue(address);
        setAddressFormModalVisible(true);
      }
    }
  };

  const handleAddressSubmit = async (values) => {
    if (isEditMode) return;

    try {
      if (editingAddress) {
        await addressAPI.update(editingAddress.id, values);
        message.success('地址更新成功');

        if (selectedAddressId === editingAddress.id) {
          form.setFieldsValue({
            customer_name: values.customer_name,
            customer_email: values.customer_email,
            customer_phone: values.customer_phone,
            dest_country: values.country,
            shipping_address: values.address,
          });

          setRefreshKey(prev => prev + 1);
          setTimeout(() => {
            onCountryChange(values.country);
          }, 100);
        }
      } else {
        if (selectedUserId) {
          await addressAPI.createForUser(selectedUserId, values);
          message.success('地址创建成功');
        } else {
          message.error('请先选择用户');
          return;
        }
      }

      setAddressFormModalVisible(false);
      addressForm.resetFields();
      fetchSavedAddresses();
    } catch (error) {
      message.error('保存地址失败');
    }
  };

  const handleSubmit = async (values) => {
    if (!selectedUserId) {
      message.error('请选择订单归属用户');
      return;
    }

    if (!isEditMode && !selectedAddressId) {
      message.error('请选择收货地址');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...values,
        dropshipper_id: selectedUserId,
        order_items: (values.order_items || []).map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          variant_path: item.variant_path,
          freight_id: item.freight_id,
          customization_id: item.customization_id || null
        }))
      };

      if (isEditMode) {
        await orderAPI.edit(orderNumber, payload);
        message.success('订单更新成功');
      } else {
        await orderAPI.create(payload);
        message.success('订单创建成功');
      }

      navigate('/admin/orders');
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error(isEditMode ? '更新订单失败' : '创建订单失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const onProductChange = (productId, index, suppliedProductEntry = null) => {
    const productEntry = suppliedProductEntry || myProducts.find(p => p.product_id === productId);
    if (!productEntry) {
      return;
    }

    const productData = productEntry.product;
    const orderItems = form.getFieldValue('order_items');

    // 检查是否为单品（无变体）
    const isSimple = !productData?.variant_types || productData.variant_types.length === 0;

    if (isSimple) {
      // 单品情况，自动设置variant_path为空数组和selected_variant_key
      orderItems[index].variant_path = [];
      const variant = productData?.variants?.[0];
      if (variant) {
        orderItems[index].selected_variant_key = variant.id ?? variant.SKU ?? 0;
      }
      // 计算单品价格
      const price = productEntry.custom_price || variant?.price || 0;
      orderItems[index].unit_price = price;
    } else {
      // 多变体情况，重置变体选择
      orderItems[index].variant_path = undefined;
      orderItems[index].selected_variant_key = undefined;
      orderItems[index].unit_price = 0;
    }

    // 自动设置该产品对应的freight和customization
    if (productEntry.freight_id) {
      orderItems[index].freight_id = productEntry.freight_id;
    }
    if (productEntry.customization_id) {
      orderItems[index].customization_id = productEntry.customization_id;
    }

    form.setFieldsValue({ order_items: orderItems });

    // 对于单品，自动选择第一个variant
    if (isSimple && productData?.variants && Array.isArray(productData.variants) && productData.variants.length > 0) {
      const firstVariant = productData.variants[0];
      const opts = getVariantOptions(productData);
      const picked = opts.find(o => o.value === (firstVariant.id ?? firstVariant.SKU ?? 0));

      if (picked) {
        setTimeout(() => {
          form.setFieldsValue({
            order_items: orderItems.map((it, i) =>
              i === index
                ? {
                    ...it,
                    selected_variant_key: picked.value,
                    variant_path: picked.data.variant_path || [],
                    unit_price: productEntry.custom_price || picked.data.price || 0,
                    weight: picked.data.weight ?? it.weight,
                    SKU: picked.data.SKU ?? it.SKU,
                  }
                : it
            )
          });
        }, 50);
      }
    }

    // 触发价格预览更新
    setTimeout(() => {
      setRefreshKey(prev => prev + 1);
    }, 100);
  };

  const openProductSelector = (fieldIndex) => {
    setProductSelectorIndex(fieldIndex);
    setProductSelectorVisible(true);
  };

  const closeProductSelector = () => {
    setProductSelectorVisible(false);
    setProductSelectorIndex(null);
  };

  const handleProductSelected = (productId, productData) => {
    if (productSelectorIndex === null) {
      closeProductSelector();
      return;
    }

    const productEntry = {
      id: productData.id,
      product_id: productData.id,
      product: productData,
      is_active: productData.is_active,
      custom_price: null,
      freight_id: null,
      customization_id: null,
    };

    setMyProducts(prev => {
      const existingIndex = prev.findIndex(p => p.product_id === productId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...productEntry,
        };
        return updated;
      }
      return [...prev, productEntry];
    });

    const orderItems = form.getFieldValue('order_items') || [];
    const currentItem = { ...(orderItems[productSelectorIndex] || {}) };
    currentItem.product_id = productId;
    orderItems[productSelectorIndex] = currentItem;
    form.setFieldsValue({ order_items: orderItems });
    form.validateFields([['order_items', productSelectorIndex, 'product_id']]).catch(() => {});

    setTimeout(() => {
      onProductChange(productId, productSelectorIndex, productEntry);
    }, 0);

    closeProductSelector();
  };


  // 获取产品的变体选项
  const getVariantOptions = (product) => {
    if (!product || !product.variants) return [];

    // 新的扁平化格式：判断是否为单品
    const isSimple = !product.variant_types || product.variant_types.length === 0;
    
    if (isSimple) {
      // 单品：variants是数组，取第一个元素
      if (Array.isArray(product.variants) && product.variants.length > 0) {
        const variant = product.variants[0];
        return [{ 
          value: variant.id ?? variant.SKU ?? 0, // 用 id 或 SKU 作稳定值
          label: '单品', 
          data: variant 
        }];
      }
      return [];
    }

    // 多变体：variants是数组，每个元素有variant_path
    if (Array.isArray(product.variants)) {
      return product.variants.map((variant, index) => ({
        value: variant.id ?? variant.SKU ?? index, // 首选 id，其次 SKU，最后才退回 index
        label: (variant.variant_path || []).join(' - '),
        data: variant
      }));
    }

    return [];
  };

  // 获取变体信息的辅助函数
  const getVariantInfo = (variants, variantPath) => {
    if (!variants || !Array.isArray(variants)) return null;
    
    if (!variantPath || variantPath.length === 0) {
      // 单品情况，查找没有variant_path或variant_path为空的项
      for (const variant of variants) {
        if (!variant.variant_path || variant.variant_path.length === 0) {
          return variant;
        }
      }
      return null;
    } else {
      // 多变体情况，根据variant_path查找匹配的项
      for (const variant of variants) {
        const variantVariantPath = variant.variant_path || [];
        if (JSON.stringify(variantVariantPath) === JSON.stringify(variantPath)) {
          return variant;
        }
      }
      return null;
    }
  };

  // 获取产品封面图片URL
  const getProductCoverImage = (product) => {
    if (!product?.images?.length) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
    }
    
    // 优先显示非变体图片作为封面
    const coverImage = product.images.find(img => !img.for_variants) || product.images[0];
    return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
  };

  // 获取变体图片URL
  const getVariantImage = (product, variantPath) => {
    if (!product?.images?.length || !variantPath || variantPath.length === 0) return null;
    
    const variantInfo = getVariantInfo(product.variants, variantPath);
    if (!variantInfo?.image) return null;
    
    return `${API_BASE_URL}/products/${product.id}/images/${variantInfo.image}`;
  };

  // 货币转换辅助函数
  const convertToUSD = (amount, currency) => {
    if (currency === 'USD') {
      return amount;
    }
    
    if (exchangeRates[currency]) {
      // rate_to_usd 表示 1 USD = X 目标货币，所以转换为USD需要除以这个值
      const usdRate = 1.0 / exchangeRates[currency];
      return amount * usdRate;
    }
    
    return amount; // 如果没有汇率信息，返回原值
  };

  // 新增的辅助函数
  const onCountryChange = (countryCode) => {
    // Force re-render to update freight validation status for all items
    setRefreshKey(prev => prev + 1);
    
    // Also trigger validation for all order items
    const orderItems = form.getFieldValue('order_items') || [];
    orderItems.forEach((item, index) => {
      if (item.freight_id) {
        // Re-calculate freight validation for each item
        setTimeout(() => {
          onFreightChange(item.freight_id, index);
        }, 50);
      }
    });
  };

  const onFreightChange = (freightId, itemIndex) => {
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    
    if (freightId && destCountry) {
      const freight = freightChannels.find(f => f.id === freightId);
      if (freight && freight.data[destCountry]) {
        // Freight is available for this country
        calculateItemPrice(itemIndex);
      } else {
        // Don't show popup message, let the red text below handle the error display
        calculateItemPrice(itemIndex);
      }
    } else {
      calculateItemPrice(itemIndex);
    }
    
    // 触发价格预览更新
    setTimeout(() => {
      setRefreshKey(prev => prev + 1);
    }, 100);
  };

  const calculateItemPrice = (itemIndex) => {
    // This is just for display purposes - actual calculation is done in backend
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    const item = orderItems[itemIndex];
    
    if (!item || !item.product_id || (item.variant_path === undefined)) return null;
    
    // Get product price
    const product = myProducts.find(p => p.product_id === item.product_id);
    let itemPrice = 0;
    
    if (product?.product?.variants && Array.isArray(product.product.variants)) {
      // 根据variant_path找到对应的variant
      const variant = product.product.variants.find(v => 
        JSON.stringify(v.variant_path || []) === JSON.stringify(item.variant_path || [])
      );
      
      // 优先使用dropshipper的自定义价格，然后使用variant价格
      if (product?.custom_price !== undefined && product?.custom_price !== null) {
        itemPrice = product.custom_price;
      } else {
        itemPrice = variant?.price || 0;
      }
    }
    
    // Get customization price
    let customizationPrice = 0;
    if (item.customization_id) {
      const customization = myCustomizations.find(c => c.id === item.customization_id);
      customizationPrice = customization?.price_prepacket || 0;
    }
    
    // Calculate freight price (accurate calculation based on weight)
    let freightPrice = 0;
    if (item.freight_id && destCountry) {
      const freight = freightChannels.find(f => f.id === item.freight_id);
      if (freight && freight.data[destCountry]) {
        const countryData = freight.data[destCountry];
        if (countryData.weight) {
          // 计算总重量
          const variant = product?.product?.variants?.find(v => 
            JSON.stringify(v.variant_path || []) === JSON.stringify(item.variant_path || [])
          );
          const itemWeight = variant?.weight || 100; // 默认100g
          const totalWeight = itemWeight * (item.quantity || 1);
          
          // 找到适合的重量段（找最小的满足条件的重量段）
          let suitableRange = null;
          for (const [weightStr, pricing] of Object.entries(countryData.weight)) {
            const weightLimit = parseInt(weightStr);
            if (totalWeight <= weightLimit) {
              if (!suitableRange || weightLimit < parseInt(suitableRange[0])) {
                suitableRange = [weightStr, pricing];
              }
            }
          }
          
          if (suitableRange) {
            const pricing = suitableRange[1];
            const prePacket = parseFloat(pricing.pre_packet || 0);
            const preKg = parseFloat(pricing.pre_kg || 0);
            const currency = pricing.currency || 'USD';
            
            // 计算运费：每包裹费用 + 每公斤费用 * 重量(kg)
            const weightKg = totalWeight / 1000.0;
            const originalFreightPrice = prePacket + (preKg * weightKg);
            
            // 转换为USD
            freightPrice = convertToUSD(originalFreightPrice, currency);
          }
        }
      }
    }
    
    const quantity = item.quantity || 1;
    
    // 计算总重量（用于显示）
    const variant = product?.product?.variants?.find(v => 
      JSON.stringify(v.variant_path || []) === JSON.stringify(item.variant_path || [])
    );
    const itemWeight = variant?.weight || 100; // 默认100g
    const totalWeight = itemWeight * quantity;
    
    return {
      itemPrice: itemPrice * quantity,
      customizationPrice: customizationPrice * quantity,
      freightPrice: freightPrice,
      totalWeight: totalWeight,
      total: (itemPrice * quantity) + (customizationPrice * quantity) + freightPrice
    };
  };

  const renderPricePreview = (itemIndex) => {
    const prices = calculateItemPrice(itemIndex);
    if (prices) {
      return `商品: $${normalizeMoney(prices.itemPrice)} | 定制: $${normalizeMoney(prices.customizationPrice)} | 运费: $${normalizeMoney(prices.freightPrice)} | 重量: ${prices.totalWeight}g | 总计: $${normalizeMoney(prices.total)}`;
    }
    return '商品: $0.00 | 定制: $0.00 | 运费: $0.00 | 重量: 0g | 总计: $0.00';
  };

  const getFreightStatus = (itemIndex) => {
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    const item = orderItems[itemIndex];
    
    if (item?.freight_id && destCountry) {
      const freight = freightChannels.find(f => f.id === item.freight_id);
      if (freight && !freight.data[destCountry]) {
        return 'error';
      }
    }
    return undefined;
  };

  const getFreightError = (itemIndex) => {
    const orderItems = form.getFieldValue('order_items') || [];
    const destCountry = form.getFieldValue('dest_country');
    const item = orderItems[itemIndex];
    
    if (item?.freight_id && destCountry) {
      const freight = freightChannels.find(f => f.id === item.freight_id);
      if (freight && !freight.data[destCountry]) {
        const countryName = countryOptions.find(c => c.value === destCountry)?.label || destCountry;
        return `无法配送到 ${countryName}，使用 ${freight.name}`;
      }
    }
    return null;
  };

  if (isEditMode && orderLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isEditMode && !orderLoading && !orderData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <div>订单不存在</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/admin/orders')}
          size="large"
        >
          返回订单列表
        </Button>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
          {isEditMode && orderData
            ? `编辑订单 #${orderData.order_number}`
            : '手动添加订单'}
        </h1>
        {isEditMode && orderData && (
          <Tag color={orderData.status === 'AWAITING_PAYMENT' ? 'orange' : 
                       orderData.status === 'CONFIRMED' ? 'blue' : 
                       orderData.status === 'CANCELLED' ? 'red' : 'green'}>
            {orderData.status === 'AWAITING_PAYMENT' ? '待付款' : 
             orderData.status === 'CONFIRMED' ? '已确认' : 
             orderData.status === 'CANCELLED' ? '已取消' : '已完成'}
          </Tag>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={32}>
          {/* Left Column - User Selection, Address and Message */}
          <Col span={10}>
            {/* User Selection */}
            <Card title="订单归属用户" style={{ marginBottom: '24px' }}>
              <Form.Item 
                label="选择用户" 
                rules={[{ required: true, message: '请选择订单归属用户' }]}
              >
                <AutoComplete
                  value={userSearchInput}
                  options={userSelectOptions}
                  onSelect={handleUserSelect}
                  onChange={(value, option) => {
                    setUserSearchInput(value);
                    if (!value) {
                      setUserOptions([]);
                      setSelectedUserId(null);
                    } else if (!option) {
                      setSelectedUserId(null);
                    }
                  }}
                  notFoundContent={userSearchLoading ? <Spin size="small" /> : null}
                  style={{ width: '100%' }}
                  filterOption={false}
                >
                  <Search
                    placeholder="输入用户名或邮箱搜索用户"
                    allowClear
                    enterButton={<SearchOutlined />}
                    loading={userSearchLoading}
                    onSearch={handleUserSearch}
                  />
                </AutoComplete>
              </Form.Item>
              
              {selectedUserId && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f0f8ff', 
                  borderRadius: '6px',
                  fontSize: '12px'
                }}>
                  <strong>说明：</strong> 选择这个订单挂在哪个用户名下，该用户可以在其订单管理页面查看和管理此订单，跟他自己创建的订单一样。
                </div>
              )}
            </Card>
            <Card title="收货地址" style={{ marginBottom: '24px' }}>
              {isEditMode ? (
                <>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="收货人姓名"
                        name="customer_name"
                        rules={[{ required: true, message: '请输入收货人姓名' }]}
                      >
                        <Input placeholder="输入收货人姓名" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="联系电话"
                        name="customer_phone"
                        rules={[{ required: true, message: '请输入联系电话' }]}
                      >
                        <Input placeholder="输入联系电话" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="邮箱（可选）"
                    name="customer_email"
                    rules={[
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                  >
                    <Input placeholder="输入邮箱地址（可选）" />
                  </Form.Item>

                  <Form.Item
                    label="国家/地区"
                    name="dest_country"
                    rules={[{ required: true, message: '请选择国家/地区' }]}
                  >
                    <Select
                      placeholder="选择国家/地区"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={onCountryChange}
                    >
                      {countryOptions.map(country => (
                        <Option key={country.value} value={country.value} label={country.label}>
                          {country.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="详细地址"
                    name="shipping_address"
                    rules={[{ required: true, message: '请输入详细地址' }]}
                  >
                    <Input.TextArea 
                      placeholder="输入详细地址" 
                      rows={3}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item label={selectedUserId ? '选择用户的已保存地址' : '选择已保存地址'}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Select
                        placeholder={selectedUserId ? '选择该用户的已保存地址' : '请先选择用户'}
                        value={selectedAddressId}
                        onChange={handleAddressSelect}
                        style={{ flex: 1 }}
                        allowClear
                        disabled={!selectedUserId}
                      >
                        {savedAddresses.map(address => (
                          <Option key={address.id} value={address.id}>
                            {address.name} - {countryOptions.find(c => c.value === address.country)?.label || address.country} - {address.address.substring(0, 50)}...
                          </Option>
                        ))}
                      </Select>
                      {selectedAddressId && (
                        <Button onClick={handleEditAddress} disabled={!selectedUserId}>
                          编辑
                        </Button>
                      )}
                      <Button onClick={handleNewAddress} disabled={!selectedUserId}>
                        + 新建
                      </Button>
                    </div>
                  </Form.Item>

                  {!selectedUserId && (
                    <div style={{ 
                      padding: '20px', 
                      textAlign: 'center', 
                      backgroundColor: '#fff', 
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px'
                    }}>
                      <p style={{ margin: 0, color: '#666' }}>
                        请先选择订单归属用户，然后选择该用户的收货地址或为其创建新地址。
                      </p>
                    </div>
                  )}

                  {selectedUserId && savedAddresses.length === 0 && (
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: '#fff7e6', 
                      border: '1px solid #ffd591',
                      borderRadius: '6px',
                      marginTop: '16px'
                    }}>
                      <p style={{ margin: 0, color: '#d46b08' }}>
                        该用户还没有保存的地址，请点击"+ 新建"为其创建地址。
                      </p>
                    </div>
                  )}

                  {selectedAddressId && (() => {
                    const selectedAddress = savedAddresses.find(addr => addr.id === selectedAddressId);
                    return selectedAddress ? (
                      <Card size="small" style={{ backgroundColor: '#f5f5f5', marginTop: '16px' }}>
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="收货人">{selectedAddress.customer_name}</Descriptions.Item>
                          <Descriptions.Item label="联系电话">{selectedAddress.customer_phone}</Descriptions.Item>
                          <Descriptions.Item label="邮箱">{selectedAddress.customer_email || '未提供'}</Descriptions.Item>
                          <Descriptions.Item label="国家/地区">{countryOptions.find(c => c.value === selectedAddress.country)?.label || selectedAddress.country}</Descriptions.Item>
                          <Descriptions.Item label="详细地址">{selectedAddress.address}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    ) : null;
                  })()}

                  {selectedAddressId && (
                    <>
                      <Form.Item name="customer_name" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="customer_email" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="customer_phone" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="dest_country" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="shipping_address" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                    </>
                  )}
                </>
              )}
            </Card>

            <Card title="订单备注">
              <Form.Item
                label="订单备注（可选）"
                name="message"
              >
                <TextArea 
                  placeholder="输入订单的特殊说明或备注..."
                  rows={4}
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Card>
          </Col>

          {/* Right Column - Order Items */}
          <Col span={14}>
            <Card title="订单商品">
              {!selectedUserId && (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  backgroundColor: '#fafafa', 
                  border: '1px dashed #d9d9d9',
                  borderRadius: '6px',
                  marginBottom: '24px'
                }}>
                  <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
                    请先选择订单归属用户，然后添加商品。
                  </p>
                </div>
              )}
              
              {selectedUserId && (
                <Form.List name="order_items">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={`${key}-${refreshKey}`} size="small" style={{ marginBottom: 16 }}>
                          {/* Product and Variant Selection */}
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item
                                {...restField}
                                name={[name, 'product_id']}
                                label="商品"
                                rules={[{ required: true, message: '请选择商品' }]}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  <Input type="hidden" />
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <Button type="dashed" onClick={() => openProductSelector(name)}>
                                      选择商品
                                    </Button>
                                    {(() => {
                                      const orderItems = form.getFieldValue('order_items') || [];
                                      const productId = orderItems[name]?.product_id;
                                      if (!productId) {
                                        return <span style={{ color: '#999' }}>当前未选择商品</span>;
                                      }
                                      const productEntry = myProducts.find(p => p.product_id === productId);
                                      const productName = productEntry?.product?.name || productId;
                                      return (
                                        <span style={{ color: '#555' }}>
                                          {productName}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              {/* Product Cover Image */}
                              {(() => {
                                const orderItems = form.getFieldValue('order_items') || [];
                                const productId = orderItems[name]?.product_id;
                                const product = myProducts.find(p => p.product_id === productId);
                                const productData = product?.product;
                                
                                if (productData) {
                                  const coverImageUrl = getProductCoverImage(productData);
                                  return (
                                    <div style={{ marginTop: '30px' }}>
                                      <img
                                        src={coverImageUrl}
                                        alt="商品封面"
                                        width={32}
                                        height={32}
                                        style={{ 
                                          objectFit: 'cover', 
                                          borderRadius: '4px',
                                          cursor: 'pointer'
                                        }}
                                        onError={(e) => {
                                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+'
                                        }}
                                      />
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </Col>
                            <Col span={10}>
                              <Form.Item
                                {...restField}
                                name={[name, 'selected_variant_key']}
                                label="规格"
                                rules={[
                                  { 
                                    validator: (_, value) => {
                                      const orderItems = form.getFieldValue('order_items') || [];
                                      const productId = orderItems[name]?.product_id;
                                      const product = myProducts.find(p => p.product_id === productId);
                                      
                                      if (!product) {
                                        return Promise.reject(new Error('请先选择商品'));
                                      }
                                      
                                      // For single product (no variants), empty array is valid
                                      const isSimple = !product.product?.variant_types || product.product.variant_types.length === 0;
                                      if (isSimple) {
                                        return Promise.resolve();
                                      }
                                      
                                      // For multi-variant products, value must not be empty
                                      if (value === undefined || value === null) {
                                        return Promise.reject(new Error('请选择规格'));
                                      }
                                      
                                      return Promise.resolve();
                                    }
                                  }
                                ]}
                              >
                                <Select 
                                  placeholder="选择规格"
                                  optionLabelProp="children"
                                  onChange={(selectedKey) => {
                                    const orderItems = form.getFieldValue('order_items') || [];
                                    const productId = orderItems[name]?.product_id;
                                    const product = myProducts.find(p => p.product_id === productId)?.product;

                                    if (!product) return;

                                    const opts = getVariantOptions(product);
                                    const picked = opts.find(o => o.value === selectedKey);
                                    if (!picked) return;

                                    // 选中后，分别写回业务字段
                                    form.setFieldsValue({
                                      order_items: orderItems.map((it, i) =>
                                        i === name
                                          ? {
                                              ...it,
                                              variant_path: picked.data.variant_path || [],
                                              unit_price: product.custom_price || picked.data.price || 0,
                                              weight: picked.data.weight ?? it.weight,
                                              SKU: picked.data.SKU ?? it.SKU,
                                            }
                                          : it
                                      )
                                    });

                                    // 触发价格预览更新
                                    setTimeout(() => {
                                      setRefreshKey(prev => prev + 1);
                                    }, 100);
                                  }}
                                  disabled={!form.getFieldValue(['order_items', name, 'product_id'])}
                                >
                                  {(() => {
                                    const orderItems = form.getFieldValue('order_items') || [];
                                    const productId = orderItems[name]?.product_id;
                                    const product = myProducts.find(p => p.product_id === productId)?.product;

                                    return getVariantOptions(product).map(option => (
                                      <Option key={option.value} value={option.value}>
                                        {`${option.label} - $${normalizeMoney(option.data.price ?? '0')} - ${option.data.weight ?? 100}g - SKU: ${option.data.SKU || 'N/A'}`}
                                      </Option>
                                    ));
                                  })()}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              {/* Variant Image */}
                              {(() => {
                                const orderItems = form.getFieldValue('order_items') || [];
                                const productId = orderItems[name]?.product_id;
                                const variantPath = orderItems[name]?.variant_path;
                                const product = myProducts.find(p => p.product_id === productId);
                                const productData = product?.product;
                                
                                if (productData && variantPath !== undefined && Array.isArray(variantPath) && variantPath.length > 0) {
                                  const variantImageUrl = getVariantImage(productData, variantPath);
                                  if (variantImageUrl) {
                                    return (
                                      <div style={{ marginTop: '30px' }}>
                                        <img
                                          src={variantImageUrl}
                                          alt="规格图片"
                                          width={32}
                                          height={32}
                                          style={{ 
                                            objectFit: 'cover', 
                                            borderRadius: '4px',
                                            border: '2px solid #ff6b35',
                                            cursor: 'pointer'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    );
                                  }
                                }
                                return null;
                              })()}
                            </Col>
                          </Row>
                          
                          {/* Freight and Customization Selection */}
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item
                                {...restField}
                                name={[name, 'freight_id']}
                                label="物流渠道"
                                rules={[{ required: true, message: '请选择物流渠道' }]}
                              >
                                <Select 
                                  placeholder="选择物流渠道"
                                  onChange={(value) => onFreightChange(value, name)}
                                  status={getFreightStatus(name)}
                                >
                                  {(() => {
                                    // 获取当前选中的产品
                                    const orderItems = form.getFieldValue('order_items') || [];
                                    const productId = orderItems[name]?.product_id;
                                    const product = myProducts.find(p => p.product_id === productId);
                                    
                                    // 只显示商品支持的物流渠道
                                    const supportedChannelIds = product?.product?.supported_freight_channels || [];
                                    const availableChannels = freightChannels.filter(freight => 
                                      supportedChannelIds.includes(freight.id)
                                    );
                                    
                                    return availableChannels.map(freight => (
                                      <Option key={freight.id} value={freight.id}>
                                        {freight.name}
                                      </Option>
                                    ));
                                  })()}
                                </Select>
                              </Form.Item>
                              {getFreightError(name) && (
                                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '-16px', marginBottom: '8px' }}>
                                  {getFreightError(name)}
                                </div>
                              )}
                              {/* 显示商品支持的渠道数量提示 */}
                              {(() => {
                                const orderItems = form.getFieldValue('order_items') || [];
                                const productId = orderItems[name]?.product_id;
                                const product = myProducts.find(p => p.product_id === productId);
                                const supportedChannelIds = product?.product?.supported_freight_channels || [];
                                
                                if (productId && supportedChannelIds.length === 0) {
                                  return (
                                    <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '-8px', marginBottom: '8px' }}>
                                      该商品未配置支持的物流渠道，请先在商品管理中配置。
                                    </div>
                                  );
                                } 
                                return null;
                              })()}
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                {...restField}
                                name={[name, 'customization_id']}
                                label="定制服务（可选）"
                              >
                                <Select 
                                  placeholder="选择定制服务"
                                  allowClear
                                  onChange={() => {
                                    calculateItemPrice(name);
                                    setTimeout(() => setRefreshKey(prev => prev + 1), 100);
                                  }}
                                >
                                  {myCustomizations.map(customization => (
                                    <Option key={customization.id} value={customization.id}>
                                      {customization.customization_name} - {normalizeMoney(customization.price_prepacket ?? '0')}/包
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                {...restField}
                                name={[name, 'quantity']}
                                label="数量"
                                rules={[{ required: true, message: '请输入数量' }]}
                              >
                                <InputNumber 
                                  min={1} 
                                  style={{ width: '100%' }}
                                  onChange={() => {
                                    calculateItemPrice(name);
                                    setTimeout(() => setRefreshKey(prev => prev + 1), 100);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          
                          {/* Price Preview and Remove Button */}
                          <Row gutter={16} align="middle">
                            <Col span={20}>
                              <div style={{ 
                                padding: '8px 12px', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                <strong>价格预览：</strong> {renderPricePreview(name)}
                              </div>
                            </Col>
                            <Col span={4}>
                              <Button 
                                type="text" 
                                danger 
                                onClick={() => remove(name)}
                                disabled={fields.length === 1}
                                block
                              >
                                移除
                              </Button>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Form.Item>
                        <Button 
                          type="dashed" 
                          onClick={() => add({ 
                            product_id: undefined, 
                            variant_path: undefined, 
                            selected_variant_key: undefined,
                            quantity: 1, 
                            freight_id: undefined,
                            customization_id: undefined
                          })} 
                          block 
                          icon={<PlusOutlined />}
                        >
                          添加商品
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              )}

              {/* Action Buttons */}
              <div style={{ marginTop: '24px', textAlign: 'right' }}>
                <Button 
                  size="large" 
                  onClick={() => navigate('/admin/orders')}
                  style={{ marginRight: '12px' }}
                >
                  取消
                </Button>
                <Button 
                  type="primary" 
                  size="large"
                  htmlType="submit"
                  loading={loading}
                  style={{ backgroundColor: '#ff8c00', borderColor: '#ff8c00' }}
                >
                  {isEditMode ? '更新订单' : '创建订单'}
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      </Form>

      <Modal
        title="选择商品"
        open={productSelectorVisible}
        onCancel={closeProductSelector}
        footer={null}
        width={780}
        destroyOnClose
      >
        <ProductSearchSelector
          multiple={false}
          onSelect={handleProductSelected}
        />
      </Modal>

      {!isEditMode && (
        <Modal
          title={editingAddress ? '编辑地址' : '添加新地址'}
          open={addressFormModalVisible}
          onCancel={() => {
            setAddressFormModalVisible(false);
            addressForm.resetFields();
            setEditingAddress(null);
          }}
          onOk={() => addressForm.submit()}
          okText={editingAddress ? '更新' : '创建'}
          okButtonProps={{ style: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' } }}
        >
          <Form
            form={addressForm}
            layout="vertical"
            onFinish={handleAddressSubmit}
          >
            <Form.Item
              label="地址名称/标签"
              name="name"
              rules={[{ required: true, message: '请输入地址名称' }]}
            >
              <Input placeholder="例如：家庭地址、办公室、仓库" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="收货人姓名"
                  name="customer_name"
                  rules={[{ required: true, message: '请输入收货人姓名' }]}
                >
                  <Input placeholder="输入收货人姓名" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="联系电话"
                  name="customer_phone"
                  rules={[{ required: true, message: '请输入联系电话' }]}
                >
                  <Input placeholder="输入联系电话" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="邮箱（可选）"
              name="customer_email"
              rules={[
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
            >
              <Input placeholder="输入邮箱地址（可选）" />
            </Form.Item>

            <Form.Item
              label="国家/地区"
              name="country"
              rules={[{ required: true, message: '请选择国家/地区' }]}
            >
              <Select
                placeholder="选择国家/地区"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {countryOptions.map(country => (
                  <Option key={country.value} value={country.value} label={country.label}>
                    {country.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="详细地址"
              name="address"
              rules={[{ required: true, message: '请输入详细地址' }]}
            >
              <Input.TextArea 
                placeholder="输入详细地址" 
                rows={3}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default AdminEditOrder;
